import { foldGreekForCompare, splitGraphemes, toNFC } from "./lib/greekText"

export enum LetterStatus {
  Unknown = 0,
  Disabled = 1,
  Letter = 2,
  LetterAndPosition = 4,
}

export type WordLength = 4 | 5 | 6

export interface GameSettings {
  wordLength: WordLength
  diacriticsStrictMode: boolean
  darkMode: boolean
}

export const DEFAULT_SETTINGS: GameSettings = {
  wordLength: 5,
  diacriticsStrictMode: false,
  darkMode: false,
}

export const MAX_ATTEMPTS = 6

export const GREEK_KEYBOARD_ROWS: string[][] = [
  ["α", "β", "γ", "δ", "ε", "ζ", "η", "θ"],
  ["ι", "κ", "λ", "μ", "ν", "ξ", "ο", "π"],
  ["ρ", "σ", "τ", "υ", "φ", "χ", "ψ", "ω"],
]

export function createEmptyBoard(wordLength: number): string[][] {
  return Array.from({ length: MAX_ATTEMPTS }, () =>
    Array.from({ length: wordLength }, () => "")
  )
}

export function createEmptyBoardStatus(wordLength: number): LetterStatus[][] {
  return Array.from({ length: MAX_ATTEMPTS }, () =>
    Array.from({ length: wordLength }, () => LetterStatus.Unknown)
  )
}

function normalizeForCompare(grapheme: string, strict: boolean): string {
  if (strict) {
    return toNFC(grapheme).toLocaleLowerCase("el")
  }

  return foldGreekForCompare(grapheme)
}

export function evaluateGuess(
  guess: string,
  correctWord: string,
  diacriticsStrictMode: boolean
): LetterStatus[] {
  const guessGraphemes = splitGraphemes(toNFC(guess))
  const answerGraphemes = splitGraphemes(toNFC(correctWord))

  const guessCompare = guessGraphemes.map((grapheme) =>
    normalizeForCompare(grapheme, diacriticsStrictMode)
  )
  const answerCompare = answerGraphemes.map((grapheme) =>
    normalizeForCompare(grapheme, diacriticsStrictMode)
  )

  const status = Array.from({ length: guessCompare.length }, () =>
    LetterStatus.Unknown
  )
  const remainderCounts = new Map<string, number>()

  for (let i = 0; i < answerCompare.length; i += 1) {
    if (guessCompare[i] === answerCompare[i]) {
      status[i] = LetterStatus.LetterAndPosition
      continue
    }

    const currentCount = remainderCounts.get(answerCompare[i]) || 0
    remainderCounts.set(answerCompare[i], currentCount + 1)
  }

  for (let i = 0; i < guessCompare.length; i += 1) {
    if (status[i] === LetterStatus.LetterAndPosition) {
      continue
    }

    const candidate = guessCompare[i]
    const available = remainderCounts.get(candidate) || 0

    if (available > 0) {
      status[i] = LetterStatus.Letter
      remainderCounts.set(candidate, available - 1)
    } else {
      status[i] = LetterStatus.Disabled
    }
  }

  return status
}

export function isWinningStatus(statuses: LetterStatus[]): boolean {
  return statuses.every((status) => status === LetterStatus.LetterAndPosition)
}

export function keyboardKeyFromGrapheme(grapheme: string): string {
  return foldGreekForCompare(grapheme).toLocaleLowerCase("el")
}

export function mergeLetterStatus(
  currentStatuses: Map<string, LetterStatus>,
  guess: string,
  guessStatus: LetterStatus[]
): Map<string, LetterStatus> {
  const nextStatuses = new Map(currentStatuses)
  const guessGraphemes = splitGraphemes(guess)

  for (let i = 0; i < guessGraphemes.length; i += 1) {
    const key = keyboardKeyFromGrapheme(guessGraphemes[i])
    const oldStatus = nextStatuses.get(key) || LetterStatus.Unknown
    const newStatus = guessStatus[i]

    if (newStatus > oldStatus) {
      nextStatuses.set(key, newStatus)
    }
  }

  return nextStatuses
}
